<template>
  <div class="mb-4 relative sm:static flex flex-col items-start self-stretch rounded-[8px] border border-[#FF9E1B] bg-[#FBFBFB] p-6 py-6 sm:flex-row">
    <!-- Product Image -->
    <div class="flex h-[320px] flex-col items-start self-stretch rounded-[8px] bg-white p-3 justify-center mt-12 sm:mt-0 sm:justify-between">
      <span class="w-full text-[#191919] font-['Visuelt_Pro'] text-[12px] font-normal leading-[130%] hidden sm:flex">{{ badgeText }}</span>
      <div class="relative self-center">
        <img :src="productImageUrl" :alt="productName" class="h-[272px] w-[320px] object-contain">
        <span class="absolute bottom-0 left-0 text-center text-[#555] font-['Visuelt_Pro'] text-[12px] font-normal leading-[130%]">{{ displayPrice }}</span>
      </div>
    </div>

    <!-- Product Details -->
    <div class="flex-1 w-full">
      <div class="max-sm:absolute max-sm:top-[24px] max-sm:left-[24px] mb-4 flex h-[24px] w-[85px] items-center justify-center gap-1 rounded-[4px] bg-white p-1">
        <Icon icon='star' class="inline-flex h-4 w-4 aspect-square items-center justify-center leading-none text-[#F7A14C]" aria-hidden="true" />
        <span class="text-center text-[#191919] font-['Visuelt_Pro'] text-[12px] font-normal leading-none">Best match</span>
      </div>
      <h3 class="text-[#000] font-['Visuelt_Pro'] text-[24px] font-normal leading-[120%] tracking-[-0.5px]">
        {{ productName }}
      </h3>

      <p class="mt-1 text-[#999999] font-['Visuelt_Pro'] text-[12px] font-normal leading-[130%]">
        {{ formattedBenefits }}
      </p>

      <div class="flex items-center mt-1">
        <StarRating class="w-fit" :rating="productRating" />
      </div>

      <div
        v-if="bestMatchDetailsContentHtml"
        class="best-match-content mt-4 hidden font-['Visuelt_Pro'] text-[20px] font-normal leading-[120%] tracking-[-0.1px] text-[#555] sm:block"
        v-html="bestMatchDetailsContentHtml"
      />
      <button
        type="button"
        class="mt-[24px] w-full rounded-[4px]  bg-[#29221D] px-4 py-3 text-center font-['Visuelt_Pro'] text-[16px] font-normal leading-[120%] tracking-[-0.04px] text-white sm:hidden"
        @click.prevent="handleMobileAddToCart"
      >
        Add to cart
      </button>
       <button
        type="button"
        class="mt-2 w-full rounded-[4px] border border-[#B9B9B9] bg-white px-4 py-3 text-center font-['Visuelt_Pro'] text-[16px] font-normal leading-[120%] tracking-[-0.04px] text-[#191919] sm:hidden"
        @click="onMobileViewProduct"
      >
        View product
      </button>
       <button
        type="button"
        class="mt-2 w-full rounded-[4px] px-4 py-3 text-center font-['Visuelt_Pro'] text-[16px] font-normal leading-[120%] tracking-[-0.04px] text-[#191919] sm:hidden"
        @click="openMobileDetailsModal"
      >
       Read product summary
      </button>
      <div class="mt-5 hidden gap-3 sm:flex">
        <button  @click.prevent="handleClick" 
        :aria-label="`${productName} add to cart opens simulated dialog`"
        :title="`${productName} add to cart opens simulated dialog`"
        type="button"
        class="flex h-[44px] w-[112px] cursor-pointer items-center justify-center gap-1 rounded-[4px] bg-[#29221D] px-4 py-3 text-sm font-medium text-white transition hover:bg-[#1f1a16]"> 
          Add to cart
        </button>
        <button @click="onProductClick" class="flex h-[44px]w-[127px] cursor-pointer items-center justify-center gap-1 rounded-[4px] border border-[#555] px-4 py-3 text-sm font-medium text-gray-800 transition hover:bg-gray-50">
          View product
        </button>
      </div>
    </div>
  </div>

    <div
  v-if="isMobileBreakpoint && isMobileDetailsModalOpen"
  class="fixed inset-0 z-50 flex items-end bg-[#000000A6] sm:hidden"
    @click.self="handleSummaryBackdropClick"
>
  <section
    class="w-full rounded-t-[24px] bg-white px-[16px] pb-[24px] pt-[12px]"
    @touchstart="handleSummaryTouchStart"
    @touchend="handleSummaryTouchEnd"
  >
    <div class="relative mb-4 h-1">
      <div class="mx-auto h-1 w-[64px] rounded-full bg-[#D9D9D9]"></div>
    </div>

    <div
      v-if="bestMatchDetailsContentHtml"
      class="best-match-content font-['Visuelt_Pro'] text-[20px] font-normal leading-[120%] tracking-[-0.1px] text-[#555]"
      v-html="bestMatchDetailsContentHtml"
    />
    <div class="mt-6 space-y-2">
      <button
        type="button"
        class="h-[44px] w-full rounded-[4px] bg-[#29221D] px-4 text-center font-['Visuelt_Pro'] text-[16px] font-normal leading-[120%] tracking-[-0.04px] text-white"
        @click.prevent="handleMobileAddToCart"
      >
        Add to cart
      </button>

      <button
        type="button"
        class="h-[44px] w-full rounded-[4px] border border-[#555] bg-white px-4 text-center font-['Visuelt_Pro'] text-[16px] font-normal leading-[120%] tracking-[-0.04px] text-[#191919]"
        @click="onMobileViewProduct"
      >
        View product
      </button>
    </div>
  </section>
</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { parseMarkdown } from '~/composables/usePdpTgpt'
import StarRating from '~/components/ui/atom/starRating.vue'
import type { FilterQueryResponse, AddToCartAnalyticsContext } from '~~/shared/types/common'
import Icon from '~/components/ui/atom/icon.vue';
import { useCartModalStore } from '~/stores/cartModalStore'
import { useGtmStore } from '~/stores/googleTagManagerStore'
import siteService from '~/composables/siteService'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

type BestMatchDetails = NonNullable<FilterQueryResponse['best_match_product']>['product']
type bestMatchDetailsContent = NonNullable<FilterQueryResponse['best_match_product']>['content']

const props = defineProps<{
  bestMatchDetails: BestMatchDetails
  bestMatchDetailsContent?: bestMatchDetailsContent 
  analyticsContext?: AddToCartAnalyticsContext
}>()
const breakPoints = useBreakpoints(breakpointsTailwind)
const isMobileBreakpoint = breakPoints.smaller('sm')

const shortDescriptionHtml = ref('')
const bestMatchDetailsContentHtml = ref('')

watch(
  () => props.bestMatchDetails.shortDescription,
  async (shortDescription) => {
    shortDescriptionHtml.value = await parseMarkdown(shortDescription)
  },
  { immediate: true }
)

watch(
  () => props.bestMatchDetailsContent,
  async (content) => {
    bestMatchDetailsContentHtml.value = await parseMarkdown(content)
  },
  { immediate: true }
)

const productImageUrl = computed(() => props.bestMatchDetails.imageUrl ?? '')
const productName = computed(() => props.bestMatchDetails.productName || props.bestMatchDetails.name || '')
const productRating = computed(() => props.bestMatchDetails.averageRating ?? 0)
const displayPrice = computed(() => {
  return props.bestMatchDetails.retailPrice || ''
})
const badgeText = computed(() => {
  return props.bestMatchDetails.customBadges?.[0] || props.bestMatchDetails.badges?.[0] || 'Exclusive'
})
const formattedBenefits = computed(() => {
  const benefits = props.bestMatchDetails.benefits ?? []
  if (benefits.length <= 2) {
    return benefits.join(', ')
  }

  return `${benefits.slice(0, 2).join(', ')} +${benefits.length - 2} more`
})

const isMobileDetailsModalOpen = ref(false)
const summaryTouchStartY = ref<number | null>(null)
const bodyScrollLockClass = 'agentic-mobile-modal-open'

const syncBodyScroll = (isOpen: boolean) => {
  document.body.style.overflow = isOpen ? 'hidden' : ''
  document.body.classList.toggle(bodyScrollLockClass, isOpen)
}

const handleSummaryTouchStart = (event: TouchEvent) => {
  summaryTouchStartY.value = event.touches[0]?.clientY ?? null
}

const handleSummaryTouchEnd = (event: TouchEvent) => {
  const startY = summaryTouchStartY.value
  const endY = event.changedTouches[0]?.clientY
  summaryTouchStartY.value = null

  if (startY === null || endY === undefined || endY - startY <= 80) {
    return
  }

  closeMobileDetailsModal()
}

watch(
  isMobileDetailsModalOpen,
  (isOpen) => {
    syncBodyScroll(isOpen)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  syncBodyScroll(false)
})

const onProductClick = () => {
  const productUrl = props.bestMatchDetails.url ?? ''
  if (productUrl) {
    useGtmStore().selectProductEvent(props.analyticsContext)
    window.location.href = productUrl
  }
}

const openMobileDetailsModal = () => {
  useGtmStore().productViewedEvent(props.analyticsContext)
  isMobileDetailsModalOpen.value = true
}

const handleSummaryBackdropClick = () => {
  closeMobileDetailsModal()
}

const closeMobileDetailsModal = () => {
  isMobileDetailsModalOpen.value = false
}

const emit = defineEmits<{
  (e: 'added'): void
}>()

const cartModalStore = useCartModalStore()
const addToCart = async (productId: number | undefined) => {
  if (!productId) {
    cartModalStore.open()
    return
  }
  try {
    const response = await siteService().addToCart(productId, 1, 0, false, props.analyticsContext) as any
    const modalData = response.lines.find(line => line.productId === productId);
    const totalQuantity = response.lines.reduce((sum, line) => sum + line.quantity, 0);
    cartModalStore.open(
      {
        name: modalData.name,
        subtotal: modalData.subtotal,
        imageUrl: modalData.imageUrl,
        quantity: modalData.quantity,
        id: `add-to-cart-${props.bestMatchDetails.id}`
      },
      totalQuantity
    )
  } catch {
    cartModalStore.open()
  }
}

const handleClick = () => {
  addToCart(props.bestMatchDetails.id)
  emit('added')
}

const handleMobileAddToCart = () => {
  handleClick()
  closeMobileDetailsModal()
}

const onMobileViewProduct = () => {
  closeMobileDetailsModal()
  onProductClick()
}

</script>